import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    BehaviorSubject,
    Subject,
    Subscription,
    takeUntil,
    timeout,
} from 'rxjs';
import DailyIframe from '@daily-co/daily-js';
import { ParticipantService } from '../participant/participant.service';
import { ScreenShareService } from '../screen-share/screen-share.service';
import { ApiService } from '../api/api.service';
import { environment } from '../../../../environments/environment';
import {
    GeminiAnalysisRequest,
    GeminiAnalysisResponse,
    TopicObservationCollection,
    VisualAnalysisConfig,
    VisualAnalysisState,
    VisualObservation,
} from '../../models/visual-analysis.model';

@Injectable({
    providedIn: 'root',
})
export class VisualAnalysisService implements OnDestroy {
    // --- Public observables for AiModeratorService to subscribe to ---
    state$ = new BehaviorSubject<VisualAnalysisState>('idle');
    immediateObservation$ = new Subject<VisualObservation>();
    batchObservations$ = new Subject<VisualObservation[]>();
    topicSummary$ = new Subject<{
        topicTitle: string;
        summary: string;
    }>();

    // --- Feature flag control ---
    private _enabled = false;

    // --- Topic-keyed visual analysis configs ---
    private configMap = new Map<string, VisualAnalysisConfig>();

    // --- Current topic state ---
    private currentTopicUID: string | null = null;
    private currentTopicTitle: string | null = null;
    private currentTopicQuestions: string[] = [];
    private currentConfig: VisualAnalysisConfig | null = null;

    // --- Observation collections per topic ---
    private topicObservations = new Map<string, TopicObservationCollection>();

    // --- Frame capture ---
    private webcamCanvas: HTMLCanvasElement | null = null;
    private webcamContext: CanvasRenderingContext2D | null = null;
    private screenCanvas: HTMLCanvasElement | null = null;
    private screenContext: CanvasRenderingContext2D | null = null;
    private webcamVideoEl: HTMLVideoElement | null = null;
    private screenVideoEl: HTMLVideoElement | null = null;
    private webcamRetryInterval: any = null;
    private webcamCaptureInterval: any = null;
    private screenCaptureInterval: any = null;

    private readonly WEBCAM_WIDTH = 640;
    private readonly WEBCAM_HEIGHT = 480;
    private readonly SCREEN_WIDTH = 1280;
    private readonly SCREEN_HEIGHT = 720;
    private readonly DEFAULT_CAPTURE_INTERVAL_MS = 3000;
    private readonly JPEG_QUALITY = 0.6;

    // --- Batch delivery ---
    private pendingBatchObservations: VisualObservation[] = [];
    private batchDeliveryInterval: any = null;
    private readonly BATCH_INTERVAL_MS = 8000;

    // --- Adaptive frame rate ---
    private consecutiveEmptyResponses = 0;
    private currentCaptureIntervalMs = this.DEFAULT_CAPTURE_INTERVAL_MS;

    // --- Concurrency control ---
    private inFlightRequests = 0;
    private readonly MAX_IN_FLIGHT = 2;
    // Bound each analysis POST so a hung request can't permanently hold an
    // in-flight slot (see captureAndAnalyze).
    private readonly ANALYSIS_REQUEST_TIMEOUT_MS = 15000;
    // Stored so a re-run of setupWebcamCapture (once per topic) can drop the
    // previous tracksUpdated subscription instead of stacking a new one each
    // topic for the whole session.
    private webcamTrackSub: Subscription | null = null;

    // --- Context for Gemini continuity ---
    private recentObservationDescriptions: string[] = [];
    private readonly MAX_RECENT_OBSERVATIONS = 3;

    // --- Cleanup ---
    private unsubscribe$ = new Subject<void>();
    private screenShareSub: Subscription | null = null;

    constructor(
        private participantService: ParticipantService,
        private screenShareService: ScreenShareService,
        private http: HttpClient,
        private apiService: ApiService,
    ) {}

    // --- Public API ---

    isEnabled(): boolean {
        return this._enabled;
    }

    setEnabled(enabled: boolean): void {
        this._enabled = enabled;
        if (!enabled) {
            this.stopAllCapture();
        }
    }

    setVisualAnalysisConfigs(configs: Map<string, VisualAnalysisConfig>): void {
        this.configMap = configs;
    }

    startTopicAnalysis(
        topicUID: string,
        topicTitle: string,
        questions: string[],
    ): void {
        console.log(
            `VisualAnalysis: startTopicAnalysis called for "${topicTitle}" (${topicUID}), enabled=${this._enabled}`,
        );

        if (!this._enabled) return;

        const config = this.configMap.get(topicUID);
        console.log(`VisualAnalysis: config for ${topicUID}:`, config);
        if (!config || !config.enabled) return;

        // Stop any existing analysis
        if (this.currentTopicUID) {
            this.stopTopicAnalysis(this.currentTopicUID);
        }

        this.currentTopicUID = topicUID;
        this.currentTopicTitle = topicTitle;
        this.currentTopicQuestions = questions;
        this.currentConfig = config;

        // Reset adaptive frame rate
        this.consecutiveEmptyResponses = 0;
        this.currentCaptureIntervalMs =
            config.captureIntervalMs || this.DEFAULT_CAPTURE_INTERVAL_MS;

        // Initialize topic observation collection
        this.topicObservations.set(topicUID, {
            topicUID,
            topicTitle,
            startTime: Date.now(),
            observations: [],
            summaryDelivered: false,
        });

        // Determine which sources to capture
        const shouldCaptureWebcam =
            config.webcam ?? config.types.includes('behavioral');
        const shouldCaptureScreen =
            config.screenShare ??
            (config.types.includes('screen_ux') ||
                config.types.includes('product_demo'));

        // Start webcam capture
        if (shouldCaptureWebcam) {
            this.setupWebcamCapture();
        }

        // Start screen capture (reactive to screen share state)
        if (shouldCaptureScreen) {
            this.setupScreenShareCapture();
        }

        // Start batch delivery
        this.startBatchDelivery();

        this.state$.next('active');
        console.log(
            `VisualAnalysis: Topic "${topicTitle}" analysis ACTIVE — webcam=${shouldCaptureWebcam}, screen=${shouldCaptureScreen}, interval=${this.currentCaptureIntervalMs}ms`,
        );
    }

    stopTopicAnalysis(topicUID: string): void {
        if (this.currentTopicUID !== topicUID) return;

        this.stopAllCapture();

        // Generate and emit summary
        const collection = this.topicObservations.get(topicUID);
        if (
            collection &&
            collection.observations.length > 0 &&
            !collection.summaryDelivered
        ) {
            const summary = this.generateSummary(collection);
            collection.summaryDelivered = true;
            this.topicSummary$.next({
                topicTitle: collection.topicTitle,
                summary,
            });
        }

        // Deliver any remaining batched observations
        this.deliverBatchedObservations();

        this.currentTopicUID = null;
        this.currentTopicTitle = null;
        this.currentTopicQuestions = [];
        this.currentConfig = null;

        this.state$.next('paused');
    }

    destroy(): void {
        this.stopAllCapture();
        this.cleanupVideoElements();
        this.topicObservations.clear();
        this.recentObservationDescriptions = [];
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.state$.next('idle');
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    // --- Frame Capture ---

    private setupWebcamCapture(): void {
        if (!this.webcamCanvas) {
            this.webcamCanvas = document.createElement('canvas');
            this.webcamCanvas.width = this.WEBCAM_WIDTH;
            this.webcamCanvas.height = this.WEBCAM_HEIGHT;
            this.webcamContext = this.webcamCanvas.getContext('2d');
        }

        const track = this.getWebcamTrack();
        if (track) {
            this.attachWebcamTrack(track);
        } else {
            console.warn(
                'VisualAnalysis: No webcam video track available yet, will poll every 1s',
            );
            // Poll for the track — tracksUpdated may not fire if tracks are already set
            let retryCount = 0;
            const maxRetries = 30; // 30 seconds max
            this.webcamRetryInterval = setInterval(() => {
                retryCount++;
                const retryTrack = this.getWebcamTrack();
                if (retryTrack) {
                    this.clearInterval(this.webcamRetryInterval);
                    this.webcamRetryInterval = null;
                    console.log(
                        `VisualAnalysis: Webcam track found after ${retryCount}s, starting capture`,
                    );
                    this.attachWebcamTrack(retryTrack);
                } else if (retryCount >= maxRetries) {
                    this.clearInterval(this.webcamRetryInterval);
                    this.webcamRetryInterval = null;
                    console.warn(
                        'VisualAnalysis: Gave up waiting for webcam track after 30s',
                    );
                }
            }, 1000);

            // Also listen for track updates as a faster path. Drop any previous
            // subscription first — setupWebcamCapture runs once per topic, and
            // without this a new subscription (tied only to unsubscribe$, which
            // fires on destroy) accumulated per topic for the whole session.
            this.webcamTrackSub?.unsubscribe();
            this.webcamTrackSub = this.participantService.tracksUpdated
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => {
                    if (!this.webcamVideoEl && this.currentConfig) {
                        const updatedTrack = this.getWebcamTrack();
                        if (updatedTrack) {
                            this.clearInterval(this.webcamRetryInterval);
                            this.webcamRetryInterval = null;
                            console.log(
                                'VisualAnalysis: Webcam track available via track update event',
                            );
                            this.attachWebcamTrack(updatedTrack);
                        }
                    }
                });
        }
    }

    private getWebcamTrack(): MediaStreamTrack | undefined {
        // Use DailyIframe.getCallInstance() — same pattern as ScreenShareService,
        // RecordingService, ParticipantService, and ChatService
        const callObject = DailyIframe.getCallInstance();

        if (!callObject) {
            console.log(
                'VisualAnalysis: getWebcamTrack - no DailyIframe call instance',
            );
            return undefined;
        }

        let participants: any;
        try {
            participants = callObject.participants();
        } catch (e) {
            console.log(
                'VisualAnalysis: getWebcamTrack - error calling participants()',
                e,
            );
            return undefined;
        }

        if (!participants) return undefined;

        const keys = Object.keys(participants);
        console.log(
            'VisualAnalysis: getWebcamTrack - call participants:',
            keys.map((k) => ({
                id: k,
                name: participants[k]?.user_name,
                local: participants[k]?.local,
                videoState: participants[k]?.tracks?.video?.state,
                hasTrack: !!participants[k]?.tracks?.video?.persistentTrack,
                isAiMod: participants[k]?.userData?.isAiModerator,
            })),
        );

        // Find any participant with a playable video track who is NOT the AI moderator
        for (const key of keys) {
            const p = participants[key];
            if (p?.userData?.isAiModerator) continue;

            const videoTrack = p?.tracks?.video?.persistentTrack;
            const videoState = p?.tracks?.video?.state;

            if (
                videoTrack &&
                (videoState === 'playable' || videoState === 'loading')
            ) {
                console.log(
                    `VisualAnalysis: Found webcam track from "${p.user_name}" (${key}, state: ${videoState})`,
                );
                return videoTrack;
            }
        }

        return undefined;
    }

    private attachWebcamTrack(track: MediaStreamTrack): void {
        // Release any previous element first — createVideoElement autoplays and
        // decodes the webcam stream, and a new one is created per topic; the old
        // decoder would otherwise keep running until GC (CPU ramps topic by
        // topic in vision-enabled interviews).
        this.clearWebcamVideoEl();
        this.webcamVideoEl = this.createVideoElement(track);
        this.webcamVideoEl.onloadedmetadata = () => {
            console.log(
                'VisualAnalysis: Webcam video element ready, starting capture interval',
            );
            this.startWebcamCaptureInterval();
        };
    }

    private setupScreenShareCapture(): void {
        if (!this.screenCanvas) {
            this.screenCanvas = document.createElement('canvas');
            this.screenCanvas.width = this.SCREEN_WIDTH;
            this.screenCanvas.height = this.SCREEN_HEIGHT;
            this.screenContext = this.screenCanvas.getContext('2d');
        }

        // React to screen share state changes
        this.screenShareSub = this.screenShareService.isSharingScreen
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isSharing) => {
                if (isSharing && this.currentConfig) {
                    this.startScreenCapture();
                } else {
                    this.stopScreenCapture();
                }
            });
    }

    private startScreenCapture(): void {
        const track = this.participantService.localParticipant?.screenTrack;
        if (!track) {
            console.warn('VisualAnalysis: No screen share track available');
            return;
        }

        this.screenVideoEl = this.createVideoElement(track);

        this.screenVideoEl.onloadedmetadata = () => {
            this.startScreenCaptureInterval();
        };
    }

    private createVideoElement(track: MediaStreamTrack): HTMLVideoElement {
        const video = document.createElement('video');
        video.srcObject = new MediaStream([track]);
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.play().catch((err) => {
            console.warn('VisualAnalysis: Video element play failed', err);
        });
        return video;
    }

    private startWebcamCaptureInterval(): void {
        this.clearInterval(this.webcamCaptureInterval);
        this.webcamCaptureInterval = setInterval(() => {
            this.captureAndAnalyze('webcam');
        }, this.currentCaptureIntervalMs);
    }

    private startScreenCaptureInterval(): void {
        this.clearInterval(this.screenCaptureInterval);
        this.screenCaptureInterval = setInterval(() => {
            this.captureAndAnalyze('screen_share');
        }, this.currentCaptureIntervalMs);
    }

    private captureFrame(source: 'webcam' | 'screen_share'): string | null {
        const videoEl =
            source === 'webcam' ? this.webcamVideoEl : this.screenVideoEl;
        const canvas =
            source === 'webcam' ? this.webcamCanvas : this.screenCanvas;
        const context =
            source === 'webcam' ? this.webcamContext : this.screenContext;

        if (!videoEl || !canvas || !context) return null;
        if (videoEl.readyState < videoEl.HAVE_METADATA) return null;

        context.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL('image/jpeg', this.JPEG_QUALITY);
        // Strip the data:image/jpeg;base64, prefix
        return dataUrl.split(',')[1] || null;
    }

    // --- Gemini Communication ---

    private captureAndAnalyze(source: 'webcam' | 'screen_share'): void {
        if (this.inFlightRequests >= this.MAX_IN_FLIGHT) return;
        if (!this.currentTopicUID || !this.currentConfig) return;

        const frame = this.captureFrame(source);
        if (!frame) {
            console.log(
                'VisualAnalysis: captureFrame returned null for',
                source,
            );
            return;
        }
        console.log(
            `VisualAnalysis: Captured frame (${source}), size: ${Math.round(frame.length / 1024)}KB, sending to Gemini...`,
        );

        const request: GeminiAnalysisRequest = {
            frame,
            source,
            topicContext: {
                topicUID: this.currentTopicUID,
                topicTitle: this.currentTopicTitle || '',
                questions: this.currentTopicQuestions,
                analysisTypes: this.currentConfig.types,
                promptContext: this.currentConfig.promptContext,
            },
            previousObservations: [...this.recentObservationDescriptions],
        };

        this.inFlightRequests++;

        this.http
            .post<GeminiAnalysisResponse>(
                `${environment.apiUrl}/v2/talk/interview-room/visual-analysis`,
                request,
                { headers: this.apiService.headers },
            )
            // Without a timeout a hung request never settles, so inFlightRequests
            // is never decremented; after MAX_IN_FLIGHT hung requests every
            // subsequent capture returns early at the guard above and the AI
            // silently stops receiving visual observations for the rest of the
            // interview. A timeout forces the error path, which frees the slot.
            .pipe(timeout({ each: this.ANALYSIS_REQUEST_TIMEOUT_MS }))
            .subscribe({
                next: (response) => {
                    this.inFlightRequests--;
                    console.log(
                        'VisualAnalysis: Gemini response:',
                        JSON.stringify(response),
                    );
                    this.processGeminiResponse(response, source);
                },
                error: (err) => {
                    this.inFlightRequests--;
                    console.error(
                        'VisualAnalysis: Gemini request FAILED:',
                        err?.status,
                        err?.message || err,
                    );
                    this.consecutiveEmptyResponses++;
                    this.adjustCaptureRate();
                },
            });
    }

    private processGeminiResponse(
        response: GeminiAnalysisResponse,
        source: 'webcam' | 'screen_share',
    ): void {
        // Coerce to an array: a null/non-object body or a non-array
        // observations field would otherwise throw (.length / for..of) in the
        // subscribe next handler.
        const observations = Array.isArray(response?.observations)
            ? response.observations
            : [];
        if (observations.length === 0) {
            this.consecutiveEmptyResponses++;
            this.adjustCaptureRate();
            return;
        }

        // Reset adaptive rate on meaningful response
        this.consecutiveEmptyResponses = 0;
        this.currentCaptureIntervalMs =
            this.currentConfig?.captureIntervalMs ||
            this.DEFAULT_CAPTURE_INTERVAL_MS;
        this.restartActiveIntervals();

        const now = Date.now();

        for (const obs of observations) {
            const observation: VisualObservation = {
                ...obs,
                timestamp: now,
                source,
            };

            // Store in topic collection
            const collection = this.topicObservations.get(
                this.currentTopicUID!,
            );
            if (collection) {
                collection.observations.push(observation);
            }

            // Update recent observations for Gemini context
            this.recentObservationDescriptions.push(observation.description);
            if (
                this.recentObservationDescriptions.length >
                this.MAX_RECENT_OBSERVATIONS
            ) {
                this.recentObservationDescriptions.shift();
            }

            // Emit to public observable
            this.observations$?.next?.(observation);

            // Route by significance
            this.routeObservation(observation);
        }
    }

    // Observable for all observations (for logging/analytics)
    observations$ = new Subject<VisualObservation>();

    private routeObservation(observation: VisualObservation): void {
        switch (observation.significance) {
            case 'high':
                this.immediateObservation$.next(observation);
                break;
            case 'medium':
                this.pendingBatchObservations.push(observation);
                break;
            case 'low':
                // Stored in topic collection only, delivered in summary
                break;
        }
    }

    // --- Adaptive Frame Rate ---

    private adjustCaptureRate(): void {
        const config = this.currentConfig;
        const baseInterval =
            config?.captureIntervalMs || this.DEFAULT_CAPTURE_INTERVAL_MS;

        if (this.consecutiveEmptyResponses >= 10) {
            this.currentCaptureIntervalMs = Math.min(baseInterval * 2.67, 8000);
        } else if (this.consecutiveEmptyResponses >= 5) {
            this.currentCaptureIntervalMs = Math.min(baseInterval * 1.67, 5000);
        }

        this.restartActiveIntervals();
    }

    private restartActiveIntervals(): void {
        if (this.webcamCaptureInterval) {
            this.startWebcamCaptureInterval();
        }
        if (this.screenCaptureInterval) {
            this.startScreenCaptureInterval();
        }
    }

    // --- Batch Delivery ---

    private startBatchDelivery(): void {
        this.clearInterval(this.batchDeliveryInterval);
        this.batchDeliveryInterval = setInterval(() => {
            this.deliverBatchedObservations();
        }, this.BATCH_INTERVAL_MS);
    }

    flushBatch(): void {
        this.deliverBatchedObservations();
    }

    private deliverBatchedObservations(): void {
        if (this.pendingBatchObservations.length === 0) return;

        const batch = [...this.pendingBatchObservations];
        this.pendingBatchObservations = [];
        this.batchObservations$.next(batch);
    }

    // --- Summary Generation ---

    private generateSummary(collection: TopicObservationCollection): string {
        const durationMs = Date.now() - collection.startTime;
        const durationMins = Math.round(durationMs / 60000);

        const highObs = collection.observations.filter(
            (o) => o.significance === 'high',
        );
        const mediumObs = collection.observations.filter(
            (o) => o.significance === 'medium',
        );

        const keyMoments = [...highObs, ...mediumObs]
            .sort((a, b) => a.timestamp - b.timestamp)
            .map((o) => {
                const elapsed = Math.round(
                    (o.timestamp - collection.startTime) / 1000,
                );
                return `${elapsed}s: ${o.description}`;
            });

        let summary = `Over ${durationMins || '<1'} minute(s), ${collection.observations.length} visual observation(s) were captured.`;

        if (keyMoments.length > 0) {
            summary += ` Key moments: ${keyMoments.join('; ')}.`;
        }

        return summary;
    }

    // --- Cleanup Utilities ---

    private stopAllCapture(): void {
        this.clearInterval(this.webcamRetryInterval);
        this.webcamRetryInterval = null;

        this.clearInterval(this.webcamCaptureInterval);
        this.webcamCaptureInterval = null;

        // Release the webcam decoder element (stopScreenCapture already handles
        // the screen one). Called on every topic transition, so without this
        // the per-topic elements accumulated as live decoders.
        this.clearWebcamVideoEl();
        this.webcamTrackSub?.unsubscribe();
        this.webcamTrackSub = null;

        this.stopScreenCapture();
        this.screenShareSub?.unsubscribe();
        this.screenShareSub = null;

        this.clearInterval(this.batchDeliveryInterval);
        this.batchDeliveryInterval = null;
    }

    private clearWebcamVideoEl(): void {
        if (this.webcamVideoEl) {
            try {
                this.webcamVideoEl.pause();
            } catch {
                // pause() can throw on a not-yet-playing element — ignore.
            }
            this.webcamVideoEl.srcObject = null;
            this.webcamVideoEl = null;
        }
    }

    private stopScreenCapture(): void {
        this.clearInterval(this.screenCaptureInterval);
        this.screenCaptureInterval = null;

        if (this.screenVideoEl) {
            try {
                this.screenVideoEl.pause();
            } catch {
                // ignore
            }
            this.screenVideoEl.srcObject = null;
            this.screenVideoEl = null;
        }
    }

    private cleanupVideoElements(): void {
        if (this.webcamVideoEl) {
            this.webcamVideoEl.srcObject = null;
            this.webcamVideoEl = null;
        }
        if (this.screenVideoEl) {
            this.screenVideoEl.srcObject = null;
            this.screenVideoEl = null;
        }
        this.webcamCanvas = null;
        this.webcamContext = null;
        this.screenCanvas = null;
        this.screenContext = null;
    }

    private clearInterval(interval: any): void {
        if (interval) {
            clearInterval(interval);
        }
    }
}
